
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Subject, Question, QuestionType, Difficulty } from '../types';
import { generateQuiz } from '../services/geminiService';
import { saveGameProgress } from '../services/gamificationService';
import Button from '../components/Button';
import confetti from 'canvas-confetti';

const QuizPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [passage, setPassage] = useState<string | null>(null);
  const [diagram, setDiagram] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [isAnswerChecked, setIsAnswerChecked] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);

  const grade = searchParams.get('grade') || '4';
  const state = searchParams.get('state') || 'TX';
  const subject = (searchParams.get('subject') as Subject) || Subject.MATH;
  const topic = searchParams.get('topic') || 'General';
  const difficulty = (searchParams.get('difficulty') as Difficulty) || Difficulty.MEDIUM;
  const isTimed = searchParams.get('timed') === 'true';
  const mastery = parseInt(searchParams.get('mastery') || '-1');


  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const data = await generateQuiz(grade, state, subject, topic, difficulty, mastery);
        if (data.questions.length === 0) throw new Error("Could not generate questions.");
        setQuestions(data.questions);
        setPassage(data.passage || null);
        setDiagram(data.diagram || null);
        if (isTimed) setTimeRemaining(data.questions.length * 60);
      } catch (err) {
        console.error(err);
        setError("Oops! We couldn't build your quiz. Try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [grade, state, subject, topic, difficulty, isTimed, mastery]);

  useEffect(() => {
    if (!isTimed || loading || quizCompleted || questions.length === 0) return;
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsTimeUp(true);
          setQuizCompleted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimed, loading, quizCompleted, questions.length]);

  useEffect(() => {
    setSelectedAnswer(null);
    setTextAnswer('');
    setIsAnswerChecked(false);
  }, [currentIndex]);

  const handleAnswerSelect = (option: string) => {
    if (isAnswerChecked) return;
    setSelectedAnswer(option);
  };

  const handleCheckAnswer = () => {
    setIsAnswerChecked(true);
    const currentQ = questions[currentIndex];
    let isCorrect = false;

    if (currentQ.type === QuestionType.FILL_IN_THE_BLANK) {
      // Normalize by removing all whitespace and converting to lowercase
      const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();
      if (normalize(textAnswer) === normalize(currentQ.correctAnswer)) isCorrect = true;
    } else {
      if (selectedAnswer === currentQ.correctAnswer) isCorrect = true;
    }

    if (isCorrect) {
      setScore(s => s + 1);
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#34D399', '#FBBF24'] });
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setQuizCompleted(true);
    const finalScore = Math.round((score / questions.length) * 100);

    // Save to Backend
    const result = await saveGameProgress(
      finalScore,
      subject,
      topic,
      difficulty,
      questions.length,
      score,
      isTimed
    );

    setPointsEarned(result.pointsEarned);

    if (finalScore >= 70) {
      confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 } });
    }
  };

  const isButtonDisabled = () => {
    const currentQ = questions[currentIndex];
    if (currentQ.type === QuestionType.FILL_IN_THE_BLANK) return textAnswer.trim().length === 0;
    return !selectedAnswer;
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (loading) return <div className="text-center p-12">Building your quiz...</div>;
  if (error) return <div className="text-center p-12 text-red-500">{error}</div>;

  if (quizCompleted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="bg-white p-8 rounded-3xl shadow-xl">
          <h1 className="text-3xl font-bold mb-4">{isTimeUp ? "Time's Up!" : "Quiz Complete!"}</h1>
          <p className="text-4xl font-bold text-brand-blue mb-4">{Math.round((score / questions.length) * 100)}%</p>
          <p className="text-green-500 font-bold mb-8">+{pointsEarned} Points</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();
  const isCorrect = currentQuestion.type === QuestionType.FILL_IN_THE_BLANK
    ? normalize(textAnswer) === normalize(currentQuestion.correctAnswer)
    : selectedAnswer === currentQuestion.correctAnswer;

  const handleExit = () => {
    if (window.confirm("Are you sure you want to exit? Your progress will be lost.")) {
      navigate('/dashboard');
    }
  };

  return (
    <div className={`mx-auto px-4 py-8 ${passage || diagram ? 'max-w-7xl' : 'max-w-3xl'}`}>
      {/* Subject and Topic Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-indigo-900 mb-1">{subject}</h1>
        <p className="text-slate-600">{topic}</p>
      </div>

      {/* Exit and Timer Row */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={handleExit}
          className="text-slate-400 hover:text-red-500 font-bold flex items-center transition-colors px-3 py-2 rounded-lg hover:bg-red-50"
        >
          <i className="fas fa-times-circle mr-2 text-lg"></i> Exit Quiz
        </button>
        {isTimed && (
          <div className="font-mono font-bold text-xl text-brand-blue bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100">
            <i className="fas fa-stopwatch mr-2"></i>
            {formatTime(timeRemaining)}
          </div>
        )}
      </div>

      {/* Main Content: Split layout if passage or diagram exists */}
      <div className={`${(passage && passage !== 'null' && passage.trim()) || (diagram && diagram !== 'null' && diagram.trim()) ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}`}>
        {/* Reading Passage (Left Side for Language Arts) */}
        {passage && passage !== 'null' && passage.trim() && (
          <div className="bg-slate-50 p-6 rounded-3xl shadow-lg border border-slate-200 h-fit sticky top-8">
            <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center">
              <i className="fas fa-book-open mr-2 text-brand-blue"></i>
              Reading Passage
            </h3>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
              {passage}
            </div>
          </div>
        )}

        {/* Math Diagram (Left Side for Math) */}
        {diagram && diagram !== 'null' && diagram.trim() && !passage && (
          <div className="bg-slate-50 p-6 rounded-3xl shadow-lg border border-slate-200 h-fit sticky top-8">
            <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center">
              <i className="fas fa-shapes mr-2 text-brand-blue"></i>
              Diagram
            </h3>
            <div
              className="w-full flex items-center justify-center"
              style={{
                maxHeight: '450px',
                minHeight: '200px'
              }}
            >
              <style>{`
                .quiz-diagram-container svg {
                  max-width: 100% !important;
                  max-height: 450px !important;
                  width: auto !important;
                  height: auto !important;
                  display: block !important;
                }
              `}</style>
              <div
                className="quiz-diagram-container w-full h-full flex items-center justify-center"
                dangerouslySetInnerHTML={{ __html: diagram }}
              />
            </div>
          </div>
        )}

        {/* Question Card (Right Side or Full Width) */}
        <div className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
          <div className="mb-4 text-sm text-slate-500 font-semibold">
            Question {currentIndex + 1} of {questions.length}
          </div>
          <h2 className="text-2xl font-bold mb-6">{currentQuestion.text}</h2>
          {currentQuestion.type === QuestionType.FILL_IN_THE_BLANK ? (
            <input type="text" value={textAnswer} onChange={e => setTextAnswer(e.target.value)} disabled={isAnswerChecked} className="w-full p-4 border-2 rounded-xl mb-4" placeholder="Answer..." />
          ) : (
            <div className="space-y-3 mb-6">
              {currentQuestion.options.map((opt, i) => (
                <button key={i} onClick={() => handleAnswerSelect(opt)} disabled={isAnswerChecked}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedAnswer === opt ? 'border-brand-blue bg-indigo-50 font-semibold' : 'border-slate-200 hover:border-slate-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
          )}

          {isAnswerChecked && (
            <div className={`p-4 rounded-xl mb-4 ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <strong>{isCorrect ? 'Correct!' : 'Incorrect.'}</strong> {currentQuestion.explanation}
            </div>
          )}

          <div className="flex justify-end">
            {!isAnswerChecked ? (
              <Button onClick={handleCheckAnswer} disabled={isButtonDisabled()}>Check Answer</Button>
            ) : (
              <Button onClick={handleNext}>{currentIndex === questions.length - 1 ? 'Finish' : 'Next'}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
